import { ScrollDirection, type ScrollDirectionType } from "./useScroll"

const BOUNDARY_EPSILON_PX = 1

const uniqueSorted = (values: number[]): number[] =>
  [...new Set(values.map((value) => Math.round(value)))].sort((a, b) => a - b)

/**
 * Collect unique card left/top edges relative to the scroller's content box.
 * Used when cards are not a uniform pitch.
 */
export const getCardAxisBoundaries = (
  canvas: HTMLElement,
  stepTargetSelector: string
): { xs: number[]; ys: number[] } => {
  const canvasRect = canvas.getBoundingClientRect()
  const cards = canvas.querySelectorAll(stepTargetSelector)

  if (cards.length === 0) {
    return { xs: [], ys: [] }
  }

  const lefts: number[] = []
  const tops: number[] = []

  cards.forEach((card) => {
    const rect = card.getBoundingClientRect()
    lefts.push(rect.left - canvasRect.left + canvas.scrollLeft)
    tops.push(rect.top - canvasRect.top + canvas.scrollTop)
  })

  return {
    xs: uniqueSorted(lefts),
    ys: uniqueSorted(tops),
  }
}

const getPitchFallbackTarget = (
  canvas: HTMLElement,
  direction: ScrollDirectionType
): number => {
  const pitchX = Math.max(canvas.clientWidth * 0.8, 1)
  const pitchY = Math.max(canvas.clientHeight * 0.8, 1)

  switch (direction) {
    case ScrollDirection.RIGHT:
      return (Math.floor(canvas.scrollLeft / pitchX) + 1) * pitchX
    case ScrollDirection.LEFT:
      return (Math.ceil(canvas.scrollLeft / pitchX) - 1) * pitchX
    case ScrollDirection.BOTTOM:
      return (Math.floor(canvas.scrollTop / pitchY) + 1) * pitchY
    case ScrollDirection.TOP:
      return (Math.ceil(canvas.scrollTop / pitchY) - 1) * pitchY
    default: {
      const _exhaustive: never = direction
      return _exhaustive
    }
  }
}

export const getStepScrollTarget = ({
  canvas,
  direction,
  stepTargetSelector,
  prefersReducedMotion,
}: {
  canvas: HTMLElement
  direction: ScrollDirectionType
  stepTargetSelector: string
  prefersReducedMotion: boolean
}): ScrollToOptions | null => {
  const { xs, ys } = getCardAxisBoundaries(canvas, stepTargetSelector)
  const behavior: ScrollBehavior = prefersReducedMotion ? "auto" : "smooth"
  const { scrollLeft, scrollTop } = canvas

  switch (direction) {
    case ScrollDirection.RIGHT: {
      const next =
        xs.find((x) => x > scrollLeft + BOUNDARY_EPSILON_PX) ??
        getPitchFallbackTarget(canvas, direction)
      return { left: Math.max(0, next), behavior }
    }
    case ScrollDirection.LEFT: {
      const prev =
        [...xs].reverse().find((x) => x < scrollLeft - BOUNDARY_EPSILON_PX) ??
        getPitchFallbackTarget(canvas, direction)
      return { left: Math.max(0, prev), behavior }
    }
    case ScrollDirection.BOTTOM: {
      const next =
        ys.find((y) => y > scrollTop + BOUNDARY_EPSILON_PX) ??
        getPitchFallbackTarget(canvas, direction)
      return { top: Math.max(0, next), behavior }
    }
    case ScrollDirection.TOP: {
      const prev =
        [...ys].reverse().find((y) => y < scrollTop - BOUNDARY_EPSILON_PX) ??
        getPitchFallbackTarget(canvas, direction)
      return { top: Math.max(0, prev), behavior }
    }
    default: {
      const _exhaustive: never = direction
      return _exhaustive
    }
  }
}

export const stepScroll = ({
  canvas,
  direction,
  stepTargetSelector,
  prefersReducedMotion,
}: {
  canvas: HTMLElement
  direction: ScrollDirectionType
  stepTargetSelector: string
  prefersReducedMotion: boolean
}): void => {
  const target = getStepScrollTarget({
    canvas,
    direction,
    stepTargetSelector,
    prefersReducedMotion,
  })
  if (!target) return
  canvas.scrollTo(target)
}
